import {StackScreenProps} from '@react-navigation/stack';
import {Box, Button, Text, VStack} from 'native-base';
import React, {useEffect, useRef, useState} from 'react';
import {ScrollView, TextInput} from 'react-native';
import NativeQRCode from 'react-native-qrcode-svg';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {AmountInput} from '../components/AmountInput';
import {KeyboardAvoidingView} from '../components/KeyboardAvoidingView';
import {useUserContext} from '../lib/authContext';
import {formatIntAmount, formatPhone} from '../lib/formatters';
import i18n from '../lib/i18n';
import {buildPaymentRequest} from '../lib/paymentRequest';
import {config} from '../stage.config';
import {Nav, NavParams} from './navigation';

const MAX_AMOUNT = config.production ? 1000 : 100000;
const QR_CODE_SIZE = 240;

export const ReceivePaymentScreen: React.FC<
  StackScreenProps<NavParams, Nav.ReceivePaymentScreen>
> = () => {
  const {phoneNumber} = useUserContext();
  const [amount, setAmount] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const isInvalid = amount > MAX_AMOUNT;

  if (isEditing) {
    return (
      <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
        <KeyboardAvoidingView offset={125}>
          <ScrollView
            contentContainerStyle={[t.flex1, t.flex, t.flexCol, t.justifyCenter]}
            keyboardShouldPersistTaps="handled"
          >
            <AmountInput
              ref={inputRef}
              onAmountChange={setAmount}
              amount={amount}
              onSubmit={() => !isInvalid && setIsEditing(false)}
              maxAmount={MAX_AMOUNT}
              returnKeyType="done"
              getMaxAmountErrorMessage={(maxAmountString: string) =>
                i18n.t('maxAmountError', {amount: maxAmountString})
              }
            />
          </ScrollView>
          <Box style={[t.p4]}>
            <Button isDisabled={isInvalid} onPress={() => setIsEditing(false)}>
              {i18n.t('showQR')}
            </Button>
          </Box>
        </KeyboardAvoidingView>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <ScrollView contentContainerStyle={[t.flexGrow, t.justifyCenter, t.p6]}>
        <VStack space={4} alignItems="center">
          <NativeQRCode value={buildPaymentRequest({phoneNumber, amount})} size={QR_CODE_SIZE} />
          {amount > 0 && <Text style={[t.text3xl, t.fontBold]}>{formatIntAmount(amount)}</Text>}
          <Text style={[t.textGray600]}>{formatPhone(phoneNumber)}</Text>
          <Text style={[t.textSm, t.textGray600]} textAlign="center">
            {i18n.t('receivePaymentHint')}
          </Text>
        </VStack>
      </ScrollView>
      <Box style={[t.p4]}>
        <Button variant="outline" onPress={() => setIsEditing(true)}>
          {i18n.t(amount > 0 ? 'changeAmount' : 'setAmount')}
        </Button>
      </Box>
    </SafeAreaView>
  );
};
